/**
 * CapacityAnalysisPrint — impression du rapport Performance « Capacity Analysis ».
 * Réplique CapacityAnalysisPage (par pool + par personne) en réutilisant
 * buildCapacity, avec les mêmes colonnes et codes couleurs que l'écran
 * (utilization vert/ambre/rouge, net margin / profit pos/neg, idle élevé rouge).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchPersonnelCostCascade } from '../../api/tdabc/personnelCostCascade';
import { getChrYearly } from '../../api/chrYearly';
import { getTimeEffective } from '../../api/hrTimeEffective';
import { listCostPools } from '../../api/tdabc/cost_pools';
import { buildCapacity } from './CapacityAnalysisPage';
import PrintReportHeader from '../hr/print/PrintReportHeader';
import '../hr/print/PeoplePrint.css';

// Formatters alignés sur la page (fr-CH).
const FMT_INT = new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 });
const FMT_2   = new Intl.NumberFormat('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtH    = (v) => FMT_INT.format(Number(v) || 0);
const fmtCHF  = (v) => FMT_INT.format(Number(v) || 0);
const fmtRate = (v) => FMT_2.format(Number(v) || 0);
const fmtPct  = (v) => `${((Number(v) || 0) * 100).toLocaleString('fr-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

// Codes couleurs identiques à CapacityAnalysisPage.css.
const utilColor = (rate) => { const n = Number(rate); return n >= 0.85 ? '#3B6D11' : n >= 0.70 ? '#BA7517' : '#A32D2D'; };
const pnlColor  = (v) => (v > 0 ? '#3B6D11' : v < 0 ? '#A32D2D' : undefined);
const IDLE_HIGH = '#A32D2D';

// Segments du Usage breakdown (mêmes couleurs que l'écran).
const SEGMENTS = [
  { key: 'pool_mgmt_h',    label: 'Supervision', color: '#9CA3AF' },
  { key: 'billable_h',     label: 'Billable',    color: '#639922' },
  { key: 'non_billable_h', label: 'Non-bill.',   color: '#D97706' },
  { key: 'idle_h',         label: 'Idle',        color: '#DC2626' },
];

// Totaux d'un jeu de lignes (mêmes formules que le footer de CapacityTable).
function totalsOf(rows) {
  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const tTr = sum('production_h'), tTf = sum('billable_h'), tNf = sum('non_billable_h'), tFull = sum('full_cost');
  return {
    production_h: tTr, billable_h: tTf, non_billable_h: tNf, idle_h: tTr - tTf - tNf,
    utilization_rate: tTr > 0 ? (tTf + tNf) / tTr : 0,
    ccr: tTr > 0 ? tFull / tTr : 0,
    bcr: tTf > 0 ? tFull / tTf : 0,
    effective_revenue: sum('effective_revenue'),
    prod_cost: sum('prod_cost'),
    net_margin: sum('net_margin'),
    cost_of_idle: sum('cost_of_idle'),
    operating_profit: sum('operating_profit'),
  };
}

export default function CapacityAnalysisPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    Promise.all([
      fetchPersonnelCostCascade(tenantId, year),
      getChrYearly(tenantId, year).then(r => { if (r?.error) throw new Error(r.error); return r.data || r; }),
      getTimeEffective(tenantId, year).then(r => (r && r.data) ? r.data : r),
      listCostPools(tenantId),
    ]).then(([cas, chr, te, cp]) => {
      if (!alive) return;
      setData({ cas, chr, te, costPools: (cp && cp.data) || cp || [] });
      if (onDataReady) onDataReady('capacity-analysis');
    }).catch(() => { if (onDataReady) onDataReady('capacity-analysis'); });
    return () => { alive = false; };
  }, [tenantId, year]); // eslint-disable-line

  const { rows, persons, kpi } = useMemo(() => {
    if (!data) return { rows: [], persons: [], kpi: {} };
    const overheadIds = new Set((data.costPools || []).filter(p => p.is_overhead_pool).map(p => p.id));
    const built = buildCapacity(data.cas, data.chr, tenantId, overheadIds, data.te);
    return { rows: built.rows, persons: built.persons, kpi: built.kpi };
  }, [data, tenantId]);

  if (!data) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;

  const metricHeads = [
    <th key="tr">Practical capacity (TR)</th>,
    <th key="bil">Billable h</th>,
    <th key="nb">Non-bill h</th>,
    <th key="idle">Idle h</th>,
    <th key="util">Utilization</th>,
    <th key="ccr">CCR</th>,
    <th key="bcr">BCR</th>,
    <th key="rev">Rev.</th>,
    <th key="prod">Prod. cost</th>,
    <th key="net">Net margin</th>,
    <th key="idlec">Idle cost</th>,
    <th key="pl">Profit / Loss</th>,
  ];

  // Cellules de valeur (idleAlwaysHigh = footer, où l'idle cost est toujours rouge).
  const valueCells = (r, idleAlwaysHigh = false) => {
    const idleHigh = idleAlwaysHigh || Number(r.cost_of_idle) > 10000;
    return (
      <>
        <td>{fmtH(r.production_h)}</td>
        <td>{fmtH(r.billable_h)}</td>
        <td>{fmtH(r.non_billable_h)}</td>
        <td>{fmtH(r.idle_h)}</td>
        <td style={{ color: utilColor(r.utilization_rate), fontWeight: 600 }}>{fmtPct(r.utilization_rate)}</td>
        <td>{fmtRate(r.ccr)}</td>
        <td>{fmtRate(r.bcr)}</td>
        <td>{Number(r.effective_revenue) > 0 ? fmtCHF(r.effective_revenue) : '—'}</td>
        <td>{fmtCHF(r.prod_cost)}</td>
        <td style={{ color: pnlColor(r.net_margin) }}>{fmtCHF(r.net_margin)}</td>
        <td style={idleHigh ? { color: IDLE_HIGH, fontWeight: 600 } : undefined}>{fmtCHF(r.cost_of_idle)}</td>
        <td style={{ color: pnlColor(r.operating_profit), fontWeight: 600 }}>{fmtCHF(r.operating_profit)}</td>
      </>
    );
  };

  const poolTotals = totalsOf(rows);
  const personTotals = totalsOf(persons);

  return (
    <>
      {/* ── Page 1 : synthèse + usage breakdown + metrics per pool ── */}
      <section className="ppl-print__section ppl-print__section--wide">
        <PrintReportHeader category="Performance" title="Capacity Analysis" year={year} tenant={activeTenant} editedAt={editedAt} />

        {/* Tuiles de synthèse */}
        <div style={{ display: 'flex', gap: 10, margin: '4px 0 14px' }}>
          {[
            { l: 'Global utilization', v: fmtPct(kpi.utilization_avg), c: utilColor(kpi.utilization_avg), s: '(billable + non-billable) / TR' },
            { l: 'Cost of non-billable', v: `${fmtCHF(kpi.cost_of_non_billable_total)} CHF`, c: '#BA7517', s: 'Σ non-billable × CCR' },
            { l: 'Cost of idle', v: `${fmtCHF(kpi.cost_of_idle_total)} CHF`, c: '#A32D2D', s: 'Σ idle × CCR' },
            { l: 'Average CCR', v: `${fmtRate(kpi.ccr_avg)} CHF/h`, c: '#2B203E', s: 'full cost / TR' },
          ].map(t => (
            <div key={t.l} style={{ flex: 1, border: '1px solid #ECE5D8', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.04em', color: '#8B8478' }}>{t.l}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.c }}>{t.v}</div>
              <div style={{ fontSize: 8.5, color: '#8B8478' }}>{t.s}</div>
            </div>
          ))}
        </div>

        {/* Usage breakdown (barres empilées HTML, en heures) */}
        {(() => {
          const maxTot = Math.max(1, ...rows.map(r => SEGMENTS.reduce((a, s) => a + (Number(r[s.key]) || 0), 0)));
          return (
            <div style={{ margin: '0 0 16px' }}>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', fontSize: 9, color: '#8B8478', marginBottom: 4 }}>
                {SEGMENTS.map(s => (
                  <span key={s.key}><span style={{ display: 'inline-block', width: 8, height: 8, background: s.color, borderRadius: 2, marginRight: 4 }} />{s.label}</span>
                ))}
                <span style={{ color: '#B8B0A2' }}>heures</span>
              </div>
              {rows.map(r => {
                const tot = SEGMENTS.reduce((a, s) => a + (Number(r[s.key]) || 0), 0);
                return (
                  <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0' }}>
                    <span style={{ width: 96, fontSize: 10, color: '#2B203E' }}>{r.pool_name}</span>
                    <span style={{ flex: 1, display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden', background: '#F3F4F6', width: `${(tot / maxTot) * 100}%` }}>
                      {SEGMENTS.map(s => {
                        const v = Number(r[s.key]) || 0;
                        if (!v) return null;
                        return (
                          <span key={s.key} style={{ width: `${(v / tot) * 100}%`, background: s.color, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            {(v / tot) > 0.08 ? fmtH(v) : ''}
                          </span>
                        );
                      })}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div style={{ fontWeight: 700, fontSize: 13, margin: '0 0 6px', color: '#2B203E' }}>Metrics per pool</div>
        <table className="ppl-print__table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Pool</th>
              {metricHeads}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td style={{ textAlign: 'left' }}>{r.pool_name}</td>
                {valueCells(r)}
              </tr>
            ))}
            <tr>
              <td style={{ textAlign: 'left' }}><strong>TOTAL ({rows.length} pools)</strong></td>
              {valueCells(poolTotals, true)}
            </tr>
          </tbody>
        </table>
      </section>

      {/* ── Page 2 : Metrics per person ── */}
      <section className="ppl-print__section ppl-print__section--wide">
        <PrintReportHeader category="Performance" title="Capacity Analysis — Metrics per person" year={year} tenant={activeTenant} editedAt={editedAt} />
        <table className="ppl-print__table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Person</th>
              <th style={{ textAlign: 'left' }}>Cost pool</th>
              {metricHeads}
            </tr>
          </thead>
          <tbody>
            {persons.map(r => (
              <tr key={r.key}>
                <td style={{ textAlign: 'left' }}>{r.person_name}</td>
                <td style={{ textAlign: 'left' }}>{r.pool_name}</td>
                {valueCells(r)}
              </tr>
            ))}
            <tr>
              <td style={{ textAlign: 'left' }} colSpan={2}><strong>TOTAL / AVERAGE ({persons.length} persons)</strong></td>
              {valueCells(personTotals, true)}
            </tr>
          </tbody>
        </table>
      </section>
    </>
  );
}
